import RemoteApi from './BaseRemoteApi'
import { getAccessToken } from '@mulesoft/monitoring-center-components'
import { User } from './models'

export default class CoreServicesRemoteApi extends RemoteApi {

  private readonly accessTokenOpts = {
    clientId: 'monitoring_center_ui'
  }

  constructor(authorization: string) {
    super('/accounts/api', authorization)
  }

  /**
   *  @return Promise with the user profile
   */
  auth(authorization?: string): Promise<Profile> {
    if (authorization) {
      this.authorization = authorization
      return this.getProfileWithoutAccessToken().then(profile => ({...profile, access_token: authorization}))
    }

    return this.getProfileWithoutAccessToken()
      .then(profile => Promise.all([profile, getAccessToken(this.accessTokenOpts)]))
      .then(results => ({...results[0], access_token: results[1]}))
  }

  fetchMonitoringRoles(orgId: string): Promise<string[]> {
    return this._post(['authorize'], {
      namespace: 'monitoringcenter',
      action: 'GET',
      resources: [
        `/organizations/${orgId}/admin`,
        `/organizations/${orgId}/viewer`
      ]
    }, true)
  }

  fetchEnvironments(orgId: string): Promise<{data: Environment[]}> {
    const cached = this.initialCache('environments')
    return cached || this._get(['organizations', orgId, 'environments'], true)
  }

  fetchUsers(orgId: string, queryParams: {search: string, limit: number, offset: number}): Promise<{ data: User[] }> {
    return this._get(['organizations', orgId, 'users'], true, undefined, queryParams)
  }

  logOut(): Promise<void> {
    return this._get(['logout'])
  }

  private getProfileWithoutAccessToken(): Promise<Profile> {
    const cached = this.initialCache('profile')
    return cached || this._get(['profile'], true)
  }
}

export type Environment = {
  id: string,
  name: string,
  organizationId: string,
  isProduction: boolean,
  type: string,
  clientId: string,
}

export type MemberOfOrganization = {
  name: string,
  id: string,
  createdAt: string,
  updatedAt: string,
  ownerId: string,
  clientId: string,
  domain: string,
  idprovider_id: string,
  isFederated: boolean,
  parentOrganizationIds: string[],
  subOrganizationIds: string[],
  tenantOrganizationIds: string[],
  parentName: null,
  parentId: null,
  isMaster: boolean,
  subscription: {
    type: string,
    expiration: null
  }
}

export type Entitlements = {
  createEnvironments: boolean,
  globalDeployment: boolean,
  createSubOrgs: boolean,
  hybridInsight: boolean,
  hybrid: {
    enabled: boolean
  },
  workerLoggingOverride: {
    enabled: boolean
  },
  mqMessages: {
    base: number,
    addOn: number,
  },
  mqRequests: {
    base: number,
    addOn: number,
  },
  vCoresDesign: {
    assigned: number,
    reassigned: number,
  },
  gateways: {
    assigned: 1
  },
  mqAdvancedFeatures: {
    enabled: boolean
  },
  cam: {
    enabled: boolean
  },
  objectStoreRequestUnits: {
    base: number,
    addOn: number,
  },
  objectStoreKeys: {
    base: number,
    addOn: number,
  },
  designCenter: {
    api: boolean,
    mozart: boolean
  },
  partnersProduction: {
    assigned: number,
  },
  partnersSandbox: {
    assigned: number,
  },
  loadBalancer: {
    assigned: number,
    reassigned: number,
  },
  externalIdentity: boolean,
  exchange2: {
    enabled: boolean
  },
  autoscaling: boolean,
  apis: {
    enabled: boolean
  },
  armAlerts: boolean,
  vCoresProduction: {
    assigned: number,
    reassigned: number,
  },
  vCoresSandbox: {
    assigned: 1,
    reassigned: number,
  },
  crowd: {
    hideApiManagerDesigner: boolean
  },
  vpcs: {
    assigned: number,
    reassigned: number,
  },
  staticIps: {
    assigned: number,
    reassigned: number,
  },
  kpiDashboard: {
    enabled: boolean
  },
  messaging: {
    assigned: number,
  },
  monitoringCenter: {
    productSKU: number,
  }
  apiMonitoring: {
    schedules: number,
  }
}

export type Organization = MemberOfOrganization & {
  properties: {},
  environments: Environment[],
  entitlements: Entitlements,
  owner: {
    id: string,
    username: string,
    firstName: string,
    lastName: string,
    email: string,
    organizationId: string,
    enabled: boolean,
    idprovider_id: string,
    createdAt: string,
    updatedAt: string,
  },
  domain: string
}

export type Profile = {
  id: string,
  createdAt: string,
  updatedAt: string,
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  idprovider_id: string,
  username: string,
  enabled: boolean,
  deleted: boolean,
  organization: Organization,
  organizationId?: String,
  organizationPreferences: { [key: string]: { defaultEnvironment: string } },
  properties: {
    cs_auth?: {
      activeOrganizationId?: string,
    }
  },
  memberOfOrganizations: MemberOfOrganization[],
  contributorOfOrganizations: MemberOfOrganization[],
  access_token?: string,
  permissions: {
    cloudhub: {
      view: boolean
    },
    api_platform: {
      view: boolean
    },
    exchange: {
      view: boolean
    },
    messaging: {
      view: boolean
    },
    dgw: {
      view: boolean
    },
    design_center: {
      view: boolean
    },
    admin: {
      view: boolean
    },
    support: {
      view: boolean
    },
    partners: {
      view: boolean
    },
    access_manager: {
      view: boolean
    },
    exchange2: {
      view: boolean
    },
    designEnvironments: {
      enabled: boolean
    },
    kpi_dashboard: {
      view: boolean
    }
  }
}
